import { memo } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";

import { useAuthData } from "./auth-data-context";

// Wraps any route that requires an authenticated partner. Three paths:
// - loading: render nothing (the brief getMe RTT). A toplevel suspense
//   boundary or skeleton can be added later — keeping it null avoids
//   showing a flicker that immediately gets replaced.
// - authenticated: render the protected outlet
// - unauthenticated: redirect to /login, preserving the attempted URL
//   in `?next=` so post-login can land them where they were headed.
export const AuthGuard = memo(function AuthGuard() {
  const { status } = useAuthData();
  const location = useLocation();

  if (status === "loading") {
    return null;
  }
  if (status === "unauthenticated") {
    // `?next=` carries the deep-linked destination through the
    // /login round-trip so a partner who hits /partners/payouts
    // unauthenticated lands back on /partners/payouts after sign-in.
    // Mirrors marketplace's VendorAuthRequired and merchant-login's
    // `next` query param. PR5b.1 only has /dashboard as a protected
    // route, so the plumbing is exercised but no consumer reads
    // `?next=` yet — that wiring lands in PR5b.2 alongside the
    // additional authenticated routes.
    const next = `${location.pathname}${location.search}${location.hash}`;
    const target =
      next === "/" || next === "/login"
        ? "/login"
        : `/login?next=${encodeURIComponent(next)}`;
    return <Navigate replace to={target} />;
  }
  return <Outlet />;
});
